import React, { useEffect, useMemo, useRef, useState } from "react";
import "./CategoryManagement.scss";
import AdminLayout from "../../Layout/AdminLayout";
import { useDispatch, useSelector } from "react-redux";
import {
  clearCategoryError,
  clearCategorySuccess,
} from "../../Features/Auth/Slice/categorySlice";
import {
  createAdminCategory,
  createAdminSubcategory,
  updateAdminCategory,
  deleteAdminCategory,
  updateAdminSubcategory,
  deleteAdminSubcategory,
  getAdminCategories,
  getAdminSubcategories,
} from "../../Features/Auth/Thunk/categoryThunk";
import { SkeletonBlock } from "../../Components/Skeleton/Skeleton";
import { showAlert, showConfirm } from "../../feedback/feedbackApi";
import { FiChevronDown, FiChevronRight, FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";

function validateSubcategoryForm(subForm) {
  const errors = {};
  if (!String(subForm.categoryId || "").trim()) {
    errors.categoryId = "Category is missing. Close and open the form again.";
  }
  if (!String(subForm.name || "").trim()) {
    errors.name = "Sub-category name is required.";
  }

  const parseNum = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : NaN;
  };

  const low = parseNum(subForm.lowestPrice);
  const avg = parseNum(subForm.averagePrice);
  const high = parseNum(subForm.highestPrice);

  if (subForm.lowestPrice === "" || subForm.lowestPrice === null || subForm.lowestPrice === undefined) {
    errors.lowestPrice = "Lowest price is required.";
  } else if (!Number.isFinite(low) || low < 0) {
    errors.lowestPrice = "Enter a valid number ≥ 0.";
  }

  if (subForm.averagePrice === "" || subForm.averagePrice === null || subForm.averagePrice === undefined) {
    errors.averagePrice = "Average price is required.";
  } else if (!Number.isFinite(avg) || avg < 0) {
    errors.averagePrice = "Enter a valid number ≥ 0.";
  }

  if (subForm.highestPrice === "" || subForm.highestPrice === null || subForm.highestPrice === undefined) {
    errors.highestPrice = "Highest price is required.";
  } else if (!Number.isFinite(high) || high < 0) {
    errors.highestPrice = "Enter a valid number ≥ 0.";
  }

  if (Number.isFinite(low) && Number.isFinite(avg) && Number.isFinite(high)) {
    if (low > avg) {
      errors.lowestPrice = errors.lowestPrice || "Lowest must be less than or equal to average.";
    }
    if (avg > high) {
      errors.averagePrice = errors.averagePrice || "Average must be less than or equal to highest.";
    }
    if (low > high) {
      errors.lowestPrice = errors.lowestPrice || "Lowest must be less than or equal to highest.";
    }
  }

  return errors;
}

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?q=80&w=1200&auto=format&fit=crop";
const SUBCATEGORY_FETCH_LIMIT = 1000;

export default function CategoryManagement() {
  const dispatch = useDispatch();
  const fileRef = useRef(null);

  const {
    categories,
    subcategories,
    loading,
    createCategoryLoading,
    createSubcategoryLoading,
    error,
    successMessage,
  } = useSelector((state) => state.category);

  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isSubModalOpen, setIsSubModalOpen] = useState(false);

  const [categoryName, setCategoryName] = useState("");
  const [categoryImage, setCategoryImage] = useState(null);
  const [categoryPreview, setCategoryPreview] = useState("");
  const [localError, setLocalError] = useState("");
  const [subFieldErrors, setSubFieldErrors] = useState({});
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingSubcategoryId, setEditingSubcategoryId] = useState(null);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState(() => new Set());

  const [, setSelectedCategoryId] = useState(null);

  const [subForm, setSubForm] = useState({
    subcategoryId: "",
    categoryId: "",
    parentSubCategoryId: "",
    name: "",
    description: "",
    lowestPrice: "",
    averagePrice: "",
    highestPrice: "",
  });

  useEffect(() => {
    dispatch(getAdminCategories({ page: 1, limit: 50 }));
    dispatch(getAdminSubcategories({ page: 1, limit: SUBCATEGORY_FETCH_LIMIT }));
  }, [dispatch]);

  useEffect(() => {
    if (successMessage === "Category created successfully") {
      setIsCategoryModalOpen(false);
      setCategoryName("");
      setCategoryImage(null);
      setCategoryPreview("");
      dispatch(clearCategorySuccess());
      dispatch(getAdminCategories({ page: 1, limit: 50 }));
    }

    if (
      successMessage === "Subcategory created successfully" ||
      successMessage === "Subcategory updated successfully" ||
      successMessage === "Subcategory deleted successfully"
    ) {
      if (successMessage === "Subcategory created successfully") {
        showAlert("Subcategory was created.", { variant: "success", title: "Saved", duration: 4200 });
      } else if (successMessage === "Subcategory updated successfully") {
        showAlert("Subcategory was saved.", { variant: "success", title: "Saved", duration: 4200 });
      } else {
        showAlert("Subcategory was deleted.", { variant: "success", title: "Removed", duration: 4200 });
      }
      setIsSubModalOpen(false);
      setSelectedCategoryId(null);
      setEditingSubcategoryId(null);
      setSubForm({
        subcategoryId: "",
        categoryId: "",
        parentSubCategoryId: "",
        name: "",
        description: "",
        lowestPrice: "",
        averagePrice: "",
        highestPrice: "",
      });
      setSubFieldErrors({});
      dispatch(clearCategorySuccess());
      dispatch(getAdminSubcategories({ page: 1, limit: SUBCATEGORY_FETCH_LIMIT }));
    }
    if (successMessage === "Category updated successfully") {
      setIsCategoryModalOpen(false);
      setEditingCategoryId(null);
      setCategoryName("");
      setCategoryImage(null);
      setCategoryPreview("");
      dispatch(clearCategorySuccess());
      dispatch(getAdminCategories({ page: 1, limit: 50 }));
    }

    if (successMessage === "Category deleted successfully") {
      dispatch(clearCategorySuccess());
    }
  }, [successMessage, dispatch]);

  const mergedCategories = useMemo(() => {
    const normalizedSubs = (subcategories || []).map((sub) => ({
      ...sub,
      uiId: sub._id || sub.id,
      name: sub.name || "Untitled Subcategory",
      level: Number(sub.level || 2),
      parentSubCategoryId: sub.parentSubCategoryId || null,
    }));

    const buildTree = (catId) => {
      const byParent = new Map();
      normalizedSubs
        .filter((sub) => String(sub.categoryId) === String(catId))
        .forEach((sub) => {
          const parentKey = sub.parentSubCategoryId ? String(sub.parentSubCategoryId) : "root";
          if (!byParent.has(parentKey)) byParent.set(parentKey, []);
          byParent.get(parentKey).push(sub);
        });

      const walk = (parentKey = "root", depth = 0) => {
        const list = byParent.get(parentKey) || [];
        return list
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((item) => ({
            ...item,
            treeDepth: depth,
            children: walk(String(item.uiId), depth + 1),
          }));
      };

      return walk("root", 0);
    };

    return (categories || []).map((cat) => {
      const catId = cat._id || cat.id;
      return {
        ...cat,
        uiId: catId,
        title: cat.title || cat.name || "Untitled Category",
        image: cat.image || "",
        subcategories: buildTree(catId),
      };
    });
  }, [categories, subcategories]);

  const totalCount = useMemo(() => mergedCategories.length, [mergedCategories]);

  const toggleCategoryExpanded = (categoryId) => {
    const id = String(categoryId || "");
    if (!id) return;
    setExpandedCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  const getLevelLabel = (level) => {
    const safeLevel = Number(level || 2);
    if (safeLevel === 2) return "Level 2";
    if (safeLevel === 3) return "Level 3";
    if (safeLevel === 4) return "Level 4";
    if (safeLevel === 5) return "Level 5";
    return `Level ${safeLevel}`;
  };

  const levelBadge = (level) => {
    const n = Math.max(2, Math.min(5, Number(level || 2)));
    return `L${n}`;
  };

  const countLevelsByCategory = (rows = []) => {
    const counts = { L1: 1, L2: 0, L3: 0, L4: 0, L5: 0 };
    const walk = (list) => {
      list.forEach((sub) => {
        const level = Math.max(2, Math.min(5, Number(sub?.level || 2)));
        const key = `L${level}`;
        counts[key] = (counts[key] || 0) + 1;
        if (Array.isArray(sub?.children) && sub.children.length > 0) {
          walk(sub.children);
        }
      });
    };
    walk(rows);
    return counts;
  };
  const categoryNameById = useMemo(() => {
    const map = new Map();
    mergedCategories.forEach((cat) => {
      map.set(String(cat.uiId), cat.title || cat.name || "Untitled Category");
    });
    return map;
  }, [mergedCategories]);

  const openCategoryModal = () => {
    dispatch(clearCategoryError());
    setEditingCategoryId(null);
    setCategoryName("");
    setCategoryImage(null);
    setCategoryPreview("");
    setLocalError("");
    setIsCategoryModalOpen(true);
  };

  const openEditCategoryModal = (item) => {
    dispatch(clearCategoryError());
    setEditingCategoryId(item.uiId);
    setCategoryName(item.title || "");
    setCategoryImage(null);
    setCategoryPreview(item.image || "");
    setLocalError("");
    setIsCategoryModalOpen(true);
  };

  const closeCategoryModal = () => {
    setIsCategoryModalOpen(false);
    setEditingCategoryId(null);
    setCategoryName("");
    setCategoryImage(null);
    setCategoryPreview("");
    setLocalError("");
    dispatch(clearCategoryError());
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const MAX_BYTES = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_BYTES) {
      setCategoryImage(null);
      setCategoryPreview("");
      setLocalError("File too large. Max 5 MB allowed.");
      dispatch(clearCategoryError());
      if (fileRef.current) fileRef.current.value = "";
      return;
    }

    setLocalError("");
    setCategoryImage(file);
    setCategoryPreview(URL.createObjectURL(file));
  };

  const handleSaveCategory = async () => {
    if (!categoryName.trim()) return;
    if (!editingCategoryId && !categoryImage) return;

    if (editingCategoryId) {
      await dispatch(
        updateAdminCategory({
          categoryId: editingCategoryId,
          name: categoryName.trim(),
          image: categoryImage instanceof File ? categoryImage : undefined,
        })
      );
    } else {
      await dispatch(
        createAdminCategory({
          name: categoryName.trim(),
          image: categoryImage,
        })
      );
    }
  };

  const handleGenerateThemeImage = async () => {
    if (!editingCategoryId || !categoryName.trim()) return;
    setLocalError("");
    await dispatch(
      updateAdminCategory({
        categoryId: editingCategoryId,
        name: categoryName.trim(),
        imageOverride: "",
      })
    );
  };

  const openSubcategoryModal = (categoryId, subToEdit = null, parentSub = null) => {
    dispatch(clearCategoryError());
    setSubFieldErrors({});
    setSelectedCategoryId(categoryId);
    if (subToEdit) {
      const id = subToEdit.uiId || subToEdit._id || "";
      setEditingSubcategoryId(id);
      setSubForm({
        subcategoryId: id,
        categoryId: subToEdit.categoryId || categoryId,
        parentSubCategoryId: subToEdit.parentSubCategoryId || "",
        name: subToEdit.name || "",
        description: subToEdit.description ?? "",
        lowestPrice: subToEdit.lowestPrice ?? "",
        averagePrice: subToEdit.averagePrice ?? "",
        highestPrice: subToEdit.highestPrice ?? "",
      });
    } else {
      setEditingSubcategoryId(null);
      setSubForm({
        subcategoryId: "",
        categoryId,
        parentSubCategoryId: parentSub?.uiId || "",
        name: "",
        description: "",
        lowestPrice: "",
        averagePrice: "",
        highestPrice: "",
      });
    }
    setIsSubModalOpen(true);
  };

  const closeSubcategoryModal = () => {
    setIsSubModalOpen(false);
    setSelectedCategoryId(null);
    setEditingSubcategoryId(null);
    setSubFieldErrors({});
    setSubForm({
      subcategoryId: "",
      categoryId: "",
      parentSubCategoryId: "",
      name: "",
      description: "",
      lowestPrice: "",
      averagePrice: "",
      highestPrice: "",
    });
    dispatch(clearCategoryError());
  };

  const handleSubInput = (e) => {
    const { name, value } = e.target;
    setSubForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (subFieldErrors[name]) {
      setSubFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSaveSubcategory = async () => {
    dispatch(clearCategoryError());
    const fieldErrors = validateSubcategoryForm(subForm);
    setSubFieldErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) {
      showAlert("Please correct the highlighted fields.", {
        variant: "warning",
        title: "Validation",
        duration: 4000,
      });
      return;
    }

    const payload = {
      categoryId: subForm.categoryId,
      parentSubCategoryId: subForm.parentSubCategoryId || null,
      name: subForm.name.trim(),
      description: subForm.description.trim(),
      lowestPrice: subForm.lowestPrice,
      averagePrice: subForm.averagePrice,
      highestPrice: subForm.highestPrice,
    };

    try {
      if (editingSubcategoryId) {
        await dispatch(
          updateAdminSubcategory({
            subcategoryId: editingSubcategoryId,
            ...payload,
          })
        ).unwrap();
      } else {
        await dispatch(createAdminSubcategory(payload)).unwrap();
      }
    } catch {
      /* Rejected thunk sets `error` in Redux — shown in the banner below */
    }
  };

  const handleDeleteSubcategory = async (subcategoryId) => {
    const ok = await showConfirm(
      "Are you sure you want to delete this subcategory? This cannot be undone.",
      { title: "Delete subcategory", confirmText: "Delete", danger: true }
    );
    if (!ok) return;
    await dispatch(deleteAdminSubcategory(subcategoryId));
    dispatch(getAdminCategories({ page: 1, limit: 50 }));
    dispatch(getAdminSubcategories({ page: 1, limit: SUBCATEGORY_FETCH_LIMIT }));
  };

  const handleDeleteCategory = async (categoryId) => {
    const ok = await showConfirm(
      "Are you sure you want to delete this category and all its subcategories? This cannot be undone.",
      { title: "Delete category", confirmText: "Delete", danger: true }
    );
    if (!ok) return;

    await dispatch(deleteAdminCategory(categoryId));
    dispatch(getAdminCategories({ page: 1, limit: 50 }));
    dispatch(getAdminSubcategories({ page: 1, limit: SUBCATEGORY_FETCH_LIMIT }));
  };

  const handleDeleteAllCategories = async () => {
    if (mergedCategories.length === 0) return;
    const ok = await showConfirm(
      "Are you sure you want to delete ALL categories and their subcategories? This action cannot be undone.",
      { title: "Delete all categories", confirmText: "Delete all", danger: true }
    );
    if (!ok) return;

    for (const cat of mergedCategories) {
      await dispatch(deleteAdminCategory(cat.uiId));
    }
    dispatch(getAdminCategories({ page: 1, limit: 50 }));
    dispatch(getAdminSubcategories({ page: 1, limit: SUBCATEGORY_FETCH_LIMIT }));
  };

  return (
    <AdminLayout>
      <div className="categoryPage">
        <div className="categoryHero">
          <div className="categoryHead" />
        </div>

        <div className="categoryPanel">
          <div className="categoryPanelTop categoryPanelTopFlex">
            <div className="categoryPanelTopLeft">
              <button className="allFilter" type="button">
                <span className="allFilterIcon">▾</span>
                <span>All</span>
                <span className="count">({totalCount})</span>
              </button>
              <button
                type="button"
                className="createCategoryBtn"
                onClick={openCategoryModal}
              >
                + Create New Category
              </button>
            </div>

            <button
              className="panelDeleteBtn"
              type="button"
              title="Delete all categories and subcategories"
              onClick={handleDeleteAllCategories}
              disabled={mergedCategories.length === 0}
              aria-label="Delete all categories"
            >
              <FiTrash2 size={18} strokeWidth={2} aria-hidden />
            </button>
          </div>

          {loading && (
            <div style={{ padding: "12px 4px" }}>
              {Array.from({ length: 5 }).map((_, idx) => (
                <div key={idx} style={{ marginBottom: 10 }}>
                  <SkeletonBlock width="100%" height={54} />
                </div>
              ))}
            </div>
          )}

          {!loading && error && (
            <div style={{ padding: "12px 4px", color: "red" }}>{error}</div>
          )}

          <div className="categoryList">
            {mergedCategories.map((item) => (
              (() => {
                const isExpanded = expandedCategoryIds.has(String(item.uiId));
                const levelCounts = countLevelsByCategory(item.subcategories);
                return (
                <div className="categoryCard" key={item.uiId}>
                  <div className="categoryCardBody">
                    <div className="categoryCardHeader">
                      <div className="categoryCardImageWrap">
                        <img
                          src={item.image || FALLBACK_IMAGE}
                          alt=""
                          className="categoryCardImage"
                        />
                      </div>
                      <div className="categoryCardHeaderMain">
                        <h3>{item.title}</h3>
                        <div className="categoryLevelCounts">
                          <span>{`L1 - ${levelCounts.L1}`}</span>
                          <span>{`L2 - ${levelCounts.L2}`}</span>
                          <span>{`L3 - ${levelCounts.L3}`}</span>
                          <span>{`L4 - ${levelCounts.L4}`}</span>
                          <span>{`L5 - ${levelCounts.L5}`}</span>
                        </div>
                      </div>
                      <div className="categoryCardActions">
                        <button
                          type="button"
                          className="categoryCollapseBtn"
                          onClick={() => toggleCategoryExpanded(item.uiId)}
                          title={isExpanded ? "Collapse category" : "Expand category"}
                          aria-label={isExpanded ? `Collapse ${item.title}` : `Expand ${item.title}`}
                        >
                          {isExpanded ? (
                            <FiChevronDown size={18} strokeWidth={2.5} aria-hidden />
                          ) : (
                            <FiChevronRight size={18} strokeWidth={2.5} aria-hidden />
                          )}
                        </button>
                        <button
                          type="button"
                          className="categoryEditBtn"
                          onClick={() => openEditCategoryModal(item)}
                          title="Edit category"
                          aria-label={`Edit ${item.title}`}
                        >
                          <FiEdit2 size={17} strokeWidth={2} aria-hidden />
                        </button>
                        <button
                          type="button"
                          className="categoryDeleteBtn"
                          onClick={() => handleDeleteCategory(item.uiId)}
                          title="Delete category"
                          aria-label={`Delete ${item.title}`}
                        >
                          <FiTrash2 size={17} strokeWidth={2} aria-hidden />
                        </button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="subCategoryList">
                      {(function renderSubRows(rows = []) {
                        return rows.map((sub) => (
                          <React.Fragment key={sub.uiId}>
                            <div
                              className={`subCategoryRow${sub.treeDepth > 0 ? " subCategoryRow--child" : ""} subCategoryRow--level-${Math.max(2, Math.min(5, Number(sub.level || 2)))}`}
                              style={{ paddingLeft: `${12 + sub.treeDepth * 18}px` }}
                            >
                              <span className="subCategoryName">
                                <span className="subCategoryLevelBadge">{levelBadge(sub.level || 2)}</span>
                                <span className="subCategoryNameText">{sub.name}</span>
                              </span>
                              <div className="subCategoryRowActions">
                                {(Number(sub.level || 2) < 5) && (
                                  <button
                                    type="button"
                                    className="subCategoryIconBtn subCategoryIconBtn--add"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openSubcategoryModal(item.uiId, null, sub);
                                    }}
                                    title="Add child"
                                    aria-label={`Add child under ${sub.name}`}
                                  >
                                    <FiPlus size={17} strokeWidth={2.5} aria-hidden />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="subCategoryIconBtn subCategoryIconBtn--edit"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openSubcategoryModal(item.uiId, sub);
                                  }}
                                  title="Edit"
                                  aria-label={`Edit ${sub.name}`}
                                >
                                  <FiEdit2 size={16} strokeWidth={2} aria-hidden />
                                </button>
                                <button
                                  type="button"
                                  className="subCategoryIconBtn subCategoryIconBtn--delete"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteSubcategory(sub.uiId);
                                  }}
                                  title="Delete"
                                  aria-label={`Delete ${sub.name}`}
                                >
                                  <FiTrash2 size={16} strokeWidth={2} aria-hidden />
                                </button>
                              </div>
                            </div>
                            {Array.isArray(sub.children) && sub.children.length > 0 && renderSubRows(sub.children)}
                          </React.Fragment>
                        ));
                      })(item.subcategories)}

                      <button
                        className="subCategoryBtn addSubBtn"
                        type="button"
                        onClick={() => openSubcategoryModal(item.uiId)}
                      >
                        <FiPlus className="addSubBtn__icon" size={18} strokeWidth={2.5} aria-hidden />
                        Add subcategory
                      </button>
                      </div>
                    )}
                  </div>
                </div>
                );
              })()
            ))}
          </div>
        </div>

        {/* Add Category Modal */}
        {isCategoryModalOpen && (
          <div className="cmModalOverlay" onClick={closeCategoryModal}>
            <div className="cmModal" onClick={(e) => e.stopPropagation()}>
              <div className="cmModalTop">
                <h3>{editingCategoryId ? "Edit Category" : "Add New Category"}</h3>
                <button
                  type="button"
                  className="cmCloseBtn"
                  onClick={closeCategoryModal}
                >
                  ×
                </button>
              </div>

              <div className="cmField">
                <label>Category</label>
                <input
                  type="text"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                />
              </div>

              <div className="cmField">
                <label>Upload Image</label>

                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  hidden
                />

                <button
                  type="button"
                  className="uploadBox"
                  onClick={() => fileRef.current?.click()}
                >
                  {categoryPreview ? (
                    <img
                      src={categoryPreview}
                      alt="preview"
                      className="uploadPreview"
                    />
                  ) : (
                    <>
                      <div className="uploadIcon">☁</div>
                      <p>Drop or click upload image ( Max. 5 MB )</p>
                    </>
                  )}
                </button>
              </div>

              {(localError || error) && (
                <div style={{ color: "red", marginBottom: "10px" }}>{localError || error}</div>
              )}

              <div className="cmModalActions">
                <button
                  type="button"
                  className="cancelBtn"
                  onClick={closeCategoryModal}
                >
                  Cancel
                </button>
                {editingCategoryId && (
                  <button
                    type="button"
                    className="cancelBtn"
                    onClick={handleGenerateThemeImage}
                    disabled={createCategoryLoading}
                    title="Generate HealBid themed default image"
                  >
                    Theme Image
                  </button>
                )}
                <button
                  type="button"
                  className="saveBtn"
                  onClick={handleSaveCategory}
                  disabled={createCategoryLoading}
                >
                  {createCategoryLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Subcategory Modal */}
        {isSubModalOpen && (
          <div className="cmModalOverlay" onClick={closeSubcategoryModal}>
            <div className="cmModal cmModalSub" onClick={(e) => e.stopPropagation()}>
              <div className="cmModalTop">
                <h3>{editingSubcategoryId ? "Edit Subcategory" : "Add Subcategory"}</h3>
                <button
                  type="button"
                  className="cmCloseBtn"
                  onClick={closeSubcategoryModal}
                >
                  ×
                </button>
              </div>

              <div className="cmFormGrid">
                <div className={`cmField${subFieldErrors.categoryId ? " cmField--error" : ""}`}>
                  <label>
                    Category<span className="cmLabelRequired" aria-hidden>
                      *
                    </span>
                  </label>
                  <input
                    type="text"
                    value={categoryNameById.get(String(subForm.categoryId)) || ""}
                    readOnly
                    aria-invalid={Boolean(subFieldErrors.categoryId)}
                  />
                  {subFieldErrors.categoryId && (
                    <p className="cmFieldError">{subFieldErrors.categoryId}</p>
                  )}
                </div>

                <div className="cmField">
                  <label>Sub-category</label>
                  <input
                    type="text"
                    value={
                      editingSubcategoryId
                        ? (subForm.name || "Existing sub-category")
                        : "Will be created after save"
                    }
                    readOnly
                  />
                </div>

                <div className="cmField cmFieldFull">
                  <label>Parent Sub-category (optional for Level 3–5)</label>
                  <select
                    name="parentSubCategoryId"
                    value={subForm.parentSubCategoryId}
                    onChange={handleSubInput}
                  >
                    <option value="">No parent (creates Level 2)</option>
                    {(subcategories || [])
                      .filter((sub) => String(sub.categoryId) === String(subForm.categoryId))
                      .filter((sub) => String(sub._id || sub.id) !== String(subForm.subcategoryId || ""))
                      .sort((a, b) => {
                        const levelDiff = Number(a.level || 2) - Number(b.level || 2);
                        if (levelDiff !== 0) return levelDiff;
                        return String(a.name || "").localeCompare(String(b.name || ""));
                      })
                      .map((sub) => (
                        <option key={sub._id || sub.id} value={sub._id || sub.id}>
                          {`${getLevelLabel(sub.level || 2)} - ${sub.name}`}
                        </option>
                      ))}
                  </select>
                </div>

                <div
                  className={`cmField cmFieldFull${subFieldErrors.name ? " cmField--error" : ""}`}
                >
                  <label htmlFor="cm-sub-name">
                    Sub-category name
                    <span className="cmLabelRequired" aria-hidden>
                      *
                    </span>
                  </label>
                  <input
                    id="cm-sub-name"
                    type="text"
                    name="name"
                    value={subForm.name}
                    onChange={handleSubInput}
                    autoComplete="off"
                    aria-invalid={Boolean(subFieldErrors.name)}
                    aria-describedby={subFieldErrors.name ? "cm-sub-name-err" : undefined}
                  />
                  {subFieldErrors.name && (
                    <p id="cm-sub-name-err" className="cmFieldError">
                      {subFieldErrors.name}
                    </p>
                  )}
                </div>

                <div className="cmField cmFieldFull">
                  <label htmlFor="cm-sub-desc">Description (optional)</label>
                  <textarea
                    id="cm-sub-desc"
                    name="description"
                    value={subForm.description}
                    onChange={handleSubInput}
                    rows={3}
                  />
                </div>

                <div className={`cmField${subFieldErrors.lowestPrice ? " cmField--error" : ""}`}>
                  <label htmlFor="cm-sub-low">
                    Lowest price
                    <span className="cmLabelRequired" aria-hidden>
                      *
                    </span>
                  </label>
                  <input
                    id="cm-sub-low"
                    type="number"
                    name="lowestPrice"
                    min={0}
                    step="any"
                    value={subForm.lowestPrice}
                    onChange={handleSubInput}
                    aria-invalid={Boolean(subFieldErrors.lowestPrice)}
                    aria-describedby={subFieldErrors.lowestPrice ? "cm-sub-low-err" : undefined}
                  />
                  {subFieldErrors.lowestPrice && (
                    <p id="cm-sub-low-err" className="cmFieldError">
                      {subFieldErrors.lowestPrice}
                    </p>
                  )}
                </div>

                <div className={`cmField${subFieldErrors.averagePrice ? " cmField--error" : ""}`}>
                  <label htmlFor="cm-sub-avg">
                    Average price
                    <span className="cmLabelRequired" aria-hidden>
                      *
                    </span>
                  </label>
                  <input
                    id="cm-sub-avg"
                    type="number"
                    name="averagePrice"
                    min={0}
                    step="any"
                    value={subForm.averagePrice}
                    onChange={handleSubInput}
                    aria-invalid={Boolean(subFieldErrors.averagePrice)}
                    aria-describedby={subFieldErrors.averagePrice ? "cm-sub-avg-err" : undefined}
                  />
                  {subFieldErrors.averagePrice && (
                    <p id="cm-sub-avg-err" className="cmFieldError">
                      {subFieldErrors.averagePrice}
                    </p>
                  )}
                </div>

                <div className={`cmField${subFieldErrors.highestPrice ? " cmField--error" : ""}`}>
                  <label htmlFor="cm-sub-high">
                    Highest price
                    <span className="cmLabelRequired" aria-hidden>
                      *
                    </span>
                  </label>
                  <input
                    id="cm-sub-high"
                    type="number"
                    name="highestPrice"
                    min={0}
                    step="any"
                    value={subForm.highestPrice}
                    onChange={handleSubInput}
                    aria-invalid={Boolean(subFieldErrors.highestPrice)}
                    aria-describedby={subFieldErrors.highestPrice ? "cm-sub-high-err" : undefined}
                  />
                  {subFieldErrors.highestPrice && (
                    <p id="cm-sub-high-err" className="cmFieldError">
                      {subFieldErrors.highestPrice}
                    </p>
                  )}
                </div>
              </div>

              {error && (
                <div className="cmSubBanner cmSubBanner--error" role="alert">
                  {error}
                </div>
              )}

              <div className="cmModalActions">
                <button
                  type="button"
                  className="cancelBtn"
                  onClick={closeSubcategoryModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="saveBtn"
                  onClick={handleSaveSubcategory}
                  disabled={createSubcategoryLoading}
                >
                  {createSubcategoryLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
